import {
  parseCoasterProject,
  parseCoasterProjectV1,
  parseCoasterProjectV2,
  parseCoasterProjectV3,
  parseCoasterProjectV4,
  parseCoasterProjectV5,
  parseCoasterProjectV6,
  type CoasterProject,
  type CoasterProjectV1,
  type CoasterProjectV2,
  type CoasterProjectV3,
  type CoasterProjectV4,
  type CoasterProjectV5,
  type CoasterProjectV6,
} from './project';

export class CanonicalSerializationError extends Error {
  readonly code = 'canonical_serialization_failed';

  constructor(message: string) {
    super(message);
    this.name = 'CanonicalSerializationError';
  }
}

export function canonicalStringify(value: unknown): string {
  const active = new WeakSet<object>();

  const visit = (current: unknown, path: string): string => {
    if (current === null) return 'null';
    if (typeof current === 'string' || typeof current === 'boolean') return JSON.stringify(current);
    if (typeof current === 'number') {
      if (!Number.isFinite(current))
        throw new CanonicalSerializationError(`Non-finite number at ${path}`);
      return Object.is(current, -0) ? '0' : JSON.stringify(current);
    }
    if (typeof current !== 'object')
      throw new CanonicalSerializationError(`Unsupported ${typeof current} at ${path}`);
    if (active.has(current)) throw new CanonicalSerializationError(`Circular reference at ${path}`);

    active.add(current);
    try {
      if (Array.isArray(current)) {
        const values: string[] = [];
        for (let index = 0; index < current.length; index += 1) {
          if (!Object.prototype.hasOwnProperty.call(current, index))
            throw new CanonicalSerializationError(`Sparse array at ${path}[${index}]`);
          values.push(visit(current[index], `${path}[${index}]`));
        }
        return `[${values.join(',')}]`;
      }

      const prototype = Object.getPrototypeOf(current);
      if (prototype !== Object.prototype && prototype !== null)
        throw new CanonicalSerializationError(`Non-plain object at ${path}`);
      const record = current as Record<string, unknown>;
      return `{${Object.keys(record)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${visit(record[key], `${path}.${key}`)}`)
        .join(',')}}`;
    } finally {
      active.delete(current);
    }
  };

  return visit(value, '$');
}

export function serializeCoasterProject(project: CoasterProject): string {
  return canonicalStringify(parseCoasterProject(project));
}

export async function sha256Canonical(value: unknown): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new CanonicalSerializationError('Web Crypto SHA-256 is unavailable');
  const digest = await subtle.digest(
    'SHA-256',
    new TextEncoder().encode(canonicalStringify(value)),
  );
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function hashCoasterProject(project: CoasterProject): Promise<string> {
  return sha256Canonical(parseCoasterProject(project));
}

export async function hashCoasterProjectV1(project: CoasterProjectV1): Promise<string> {
  return sha256Canonical(parseCoasterProjectV1(project));
}

export async function hashCoasterProjectV2(project: CoasterProjectV2): Promise<string> {
  return sha256Canonical(parseCoasterProjectV2(project));
}

export async function hashCoasterProjectV3(project: CoasterProjectV3): Promise<string> {
  return sha256Canonical(parseCoasterProjectV3(project));
}

export async function hashCoasterProjectV4(project: CoasterProjectV4): Promise<string> {
  return sha256Canonical(parseCoasterProjectV4(project));
}

export async function hashCoasterProjectV5(project: CoasterProjectV5): Promise<string> {
  return sha256Canonical(parseCoasterProjectV5(project));
}

export async function hashCoasterProjectV6(project: CoasterProjectV6): Promise<string> {
  return sha256Canonical(parseCoasterProjectV6(project));
}

/**
 * Verifies a stored project in the exact schema version that was written.
 * Migration must happen only after this check or a valid V1/V2 envelope would
 * appear corrupt merely because migration adds V3 fields.
 */
export async function hashStoredCoasterProject(value: unknown): Promise<string> {
  const version =
    value && typeof value === 'object' && 'schemaVersion' in value
      ? (value as { schemaVersion?: unknown }).schemaVersion
      : null;
  if (version === 1) return hashCoasterProjectV1(parseCoasterProjectV1(value));
  if (version === 2) return hashCoasterProjectV2(parseCoasterProjectV2(value));
  if (version === 3) return hashCoasterProjectV3(parseCoasterProjectV3(value));
  if (version === 4) return hashCoasterProjectV4(parseCoasterProjectV4(value));
  if (version === 5) return hashCoasterProjectV5(parseCoasterProjectV5(value));
  if (version === 6) return hashCoasterProjectV6(parseCoasterProjectV6(value));
  throw new CanonicalSerializationError('Unsupported stored coaster schema version');
}

/**
 * Hashes only inputs that can affect compilation, physics, or physical
 * clearance. Saved validation is deliberately excluded so the run can carry
 * the hash of the exact inputs that produced it without a self-reference.
 */
export async function hashCoasterTrack(project: CoasterProject): Promise<string> {
  const parsed = parseCoasterProject(project);
  return sha256Canonical({
    coasterFamilyId: parsed.coasterFamilyId,
    plot: parsed.plot,
    track: parsed.track,
    train: parsed.train,
  });
}
